// src/pages/Feed.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { matchesOptionalFilter } from "../lib/filters";

const INDUSTRIES = ["All", "AI & Automation", "Sustainability", "Supply Chain", "FinTech", "Healthcare", "Logistics", "Retail Tech", "Cybersecurity"];
const BUDGETS = ["Any", "<$50k", "$50k–$150k", "$150k–$500k", "$500k+"];
const TIMELINES = ["Any", "<1 month", "1–3 months", "3–6 months", "6+ months"];

function statusBadge(status) {
  if (status === "new") return <span className="badge badge-purple">New</span>;
  if (status === "open") return <span className="badge badge-green">Open</span>;
  if (status === "closing") return <span className="badge badge-amber">Closing soon</span>;
  return null;
}

export default function Feed() {
  const { userProfile } = useAuth();
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("All");
  const [budget, setBudget] = useState("Any");
  const [timeline, setTimeline] = useState("Any");
  const [sortBy, setSortBy] = useState("newest");
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    const q = query(
      collection(db, "needs"),
      where("status", "in", ["new", "open", "closing"]),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNeeds(data);
      // Count needs from last 7 days
      const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      setNewCount(data.filter(n => n.createdAt?.toMillis?.() > weekAgo).length);
      setLoading(false);
    });
    return unsub;
  }, []);

  const filtered = needs.filter(n => {
    if (search && !n.title?.toLowerCase().includes(search.toLowerCase()) &&
        !n.description?.toLowerCase().includes(search.toLowerCase()) &&
        !n.tags?.some(t => t.toLowerCase().includes(search.toLowerCase()))) return false;
    if (!matchesOptionalFilter(n.industry, industry)) return false;
    if (!matchesOptionalFilter(n.budget, budget)) return false;
    if (!matchesOptionalFilter(n.timeline, timeline)) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === "newest") return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
    if (sortBy === "pitches") return (b.pitchCount || 0) - (a.pitchCount || 0);
    return 0;
  });

  return (
    <div className="app-shell app-shell--split">
      <aside className="feed-filters" aria-label="Challenge filters">
        <h2 className="feed-filters-title">Filters</h2>

        <FilterSection label="Industry">
          {INDUSTRIES.map(i => (
            <FilterChip key={i} active={industry === i} onClick={() => setIndustry(i)}>{i}</FilterChip>
          ))}
        </FilterSection>

        <FilterSection label="Budget">
          {BUDGETS.map(b => (
            <FilterChip key={b} active={budget === b} onClick={() => setBudget(b)}>{b}</FilterChip>
          ))}
        </FilterSection>

        <FilterSection label="Timeline">
          {TIMELINES.map(t => (
            <FilterChip key={t} active={timeline === t} onClick={() => setTimeline(t)}>{t}</FilterChip>
          ))}
        </FilterSection>

        {(industry !== "All" || budget !== "Any" || timeline !== "Any") && (
          <button
            type="button"
            className="feed-filter-clear"
            onClick={() => { setIndustry("All"); setBudget("Any"); setTimeline("Any"); }}
          >
            Clear all filters
          </button>
        )}
      </aside>

      <main className="app-main">
        {/* New needs alert */}
        {newCount > 0 && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "var(--purple-light)", border: "1px solid #AFA9EC",
            borderRadius: "var(--radius)", padding: "10px 14px", marginBottom: 16,
            fontSize: 14, color: "var(--purple-text)"
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <span><strong>{newCount} new challenge{newCount > 1 ? "s" : ""}</strong> posted in the last 7 days</span>
          </div>
        )}

        {/* Search + sort */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, position: "relative", minWidth: 160 }}>
            <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-3)" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input className="input" style={{ paddingLeft: 32, minHeight: 44 }} placeholder="Search challenges..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="select" style={{ width: 150, minHeight: 44 }} value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="pitches">Most solutions</option>
          </select>
        </div>

        {/* Count */}
        <div style={{ fontSize: 14, color: "var(--text-3)", marginBottom: 14 }}>
          {loading ? "Loading..." : `${filtered.length} open challenge${filtered.length !== 1 ? "s" : ""}`}
        </div>

        {/* Cards */}
        {loading ? (
          <div className="page-loading"><div className="spinner" style={{ width: 28, height: 28 }}></div></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state card" style={{ padding: "48px 24px" }}>
            <h3>No challenges found</h3>
            <p>Try adjusting your filters or search term.</p>
          </div>
        ) : (
          <div className="app-card-list">
            {filtered.map(need => <NeedCard key={need.id} need={need} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function NeedCard({ need }) {
  const timeAgo = need.createdAt?.toDate ? formatDistanceToNow(need.createdAt.toDate(), { addSuffix: true }) : "recently";

  return (
    <div className="card" style={{ padding: "20px 22px", transition: "box-shadow 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "var(--shadow-md)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "var(--shadow)"}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div className="avatar" style={{
            width: 40, height: 40,
            background: need.avatarColor || "#f0f0f0",
            color: need.avatarTextColor || "var(--text-2)", fontSize: 13
          }}>{need.corpAvatar || need.corpName?.slice(0, 2).toUpperCase()}</div>
          <div>
            <div className="need-card-org">{need.corpName}</div>
            <div className="need-card-industry">{need.industry}</div>
          </div>
        </div>
        {statusBadge(need.status)}
      </div>

      <h3 className="need-card-title">{need.title}</h3>
      <p className="need-card-body">
        {need.description?.length > 220 ? need.description.slice(0, 220) + "…" : need.description}
      </p>

      {need.tags?.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {need.tags.map(t => <span key={t} className="tag">{t}</span>)}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div className="need-card-meta">
          <span>{need.budget}</span>
          <span>{need.timeline}</span>
          <span>{need.pitchCount || 0} pitches</span>
          <span>{timeAgo}</span>
        </div>
        <Link to={`/need/${need.id}`} className="btn btn-primary" style={{ fontSize: 14, padding: "8px 16px" }}>
          View & pitch →
        </Link>
      </div>
    </div>
  );
}

function FilterSection({ label, children }) {
  return (
    <div className="feed-filter-section">
      <div className="feed-filter-label">{label}</div>
      <div className="feed-filter-chips">{children}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`feed-filter-chip${active ? " is-active" : ""}`}
    >
      {children}
    </button>
  );
}
